// src/hooks/useNotifications.ts
//
// React data hook for the in-app notification bell (Story 4.9). Plain
// useState/useCallback/useEffect per the repo's hook style (useTickets/useFailures)
// — no React Query. All data access goes through notificationsService; the hook
// owns the bell's state (list, unread count, loading, error) and the two mark-read
// mutations.
//
// The bell is CHROME, not a page (Decision #6): a failed INITIAL load renders the
// bare bell (no badge) and only logs — it must NEVER raise a page-level banner or
// crash the Layout every screen depends on. The quiet failure heals on the next
// successful silent refetch. Realtime refetch is SILENT (the 1.1/4.1
// non-contradiction rule): org-wide INSERT/UPDATE events on yak_notifications drive
// a refetch, and the refetch's own recipient predicates keep the data per-user —
// event-payload inspection is deliberately NOT attempted (org SELECT means events
// fire for other users' rows too; a filtered refetch is cheaper than client-side
// triage at pilot volume).

import { useState, useEffect, useCallback } from 'react'
import { notificationsService } from '../services/notificationsService'
import { useRealtimeRefetch } from './useRealtimeRefetch'
import type { YakNotificationWithTicket } from '../types/yak'

interface UseNotificationsReturn {
  notifications: YakNotificationWithTicket[]
  unreadCount: number
  loading: boolean
  error: string | null
  markingAll: boolean
  refetch: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<
    YakNotificationWithTicket[]
  >([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [markingAll, setMarkingAll] = useState(false)

  // silent = background refetch (realtime / post-mutation): never flips loading,
  // never surfaces an error (the last-known state stays; a failure only logs).
  const fetchAll = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      const [list, count] = await Promise.all([
        notificationsService.listMine(),
        notificationsService.unreadCount(),
      ])
      setNotifications(list)
      setUnreadCount(count)
    } catch (err) {
      // The bell is chrome: on the initial load, hold the error quietly (the bell
      // renders bare, no badge) rather than banner it; on a silent refetch, keep
      // the last-known rows. Either way we log and never throw to the page.
      if (!silent) {
        setError(
          err instanceof Error ? err.message : 'Failed to load notifications'
        )
      }
      console.error('Notification fetch failed', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const silentRefetch = useCallback(() => {
    fetchAll(true)
  }, [fetchAll])

  // One channel on yak_notifications, keyed 'bell' so it coexists with 4.8's
  // useFailures subscription on /settings/failures (Task 0's collision fix).
  useRealtimeRefetch('yak_notifications', silentRefetch, 'bell')

  const markRead = useCallback(
    async (id: string) => {
      await notificationsService.markRead(id)
      await fetchAll(true)
    },
    [fetchAll]
  )

  const markAllRead = useCallback(async () => {
    setMarkingAll(true)
    try {
      await notificationsService.markAllRead()
      await fetchAll(true)
    } finally {
      setMarkingAll(false)
    }
  }, [fetchAll])

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markingAll,
    refetch: () => fetchAll(),
    markRead,
    markAllRead,
  }
}
